import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import "./css/PhoneChangeVerificationPopup.css";
import PendingVerificationAPI from "./services/PendingVerificationAPI";
import { useTranslation } from "../../../i18n/LanguageContext";

function PhoneChangeVerificationPopup({
  user,
  onClose,
  onVerificationComplete,
}) {
  const [verificationCode, setVerificationCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [pendingVerificationStatus, setPendingVerificationStatus] =
    useState(null);
  const { t } = useTranslation();

  // Get pending verification status when popup opens
  useEffect(() => {
    const fetchPendingStatus = async () => {
      try {
        console.log(
          "PhoneChangeVerificationPopup: Fetching pending verification status",
        );
        const response =
          await PendingVerificationAPI.getPendingVerificationStatus();
        console.log("PhoneChangeVerificationPopup: Full response:", response);

        // Handle different response structures - data might be in response.data or response directly
        const pendingData = response.data || response;
        console.log(
          "PhoneChangeVerificationPopup: Pending status:",
          pendingData,
        );
        setPendingVerificationStatus(pendingData);

        // Check if there's actually a pending phone change
        if (!pendingData.hasPendingPhoneChange) {
          setErrorMessage(t("usermgmt.phone.noPendingChange"));
        } else if (pendingData.phoneVerificationExpired) {
          setErrorMessage(t("usermgmt.phone.verificationExpired"));
        }
      } catch (error) {
        console.error(
          "PhoneChangeVerificationPopup: Failed to fetch pending status:",
          error,
        );
        setErrorMessage(t("usermgmt.phone.loadStatusFailed"));
      }
    };

    fetchPendingStatus();
  }, []);

  const handleChange = (e) => {
    setVerificationCode(e.target.value);
    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!verificationCode.trim()) {
      setErrorMessage(t("usermgmt.phone.enterCode"));
      return;
    }

    // Check if we have pending verification status and if phone change is actually pending
    if (
      pendingVerificationStatus &&
      !pendingVerificationStatus.hasPendingPhoneChange
    ) {
      setErrorMessage(t("usermgmt.phone.noPendingChange"));
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      console.log(
        "PhoneChangeVerificationPopup: Verifying phone change with code:",
        verificationCode,
      );

      const response =
        await PendingVerificationAPI.verifyPhoneChange(verificationCode);
      console.log(
        "PhoneChangeVerificationPopup: Phone change verification successful:",
        response,
      );

      // Show success message and close popup
      toast.success(t("usermgmt.phone.verifySuccess"), { duration: 5000 });
      onVerificationComplete();
      onClose();
    } catch (error) {
      console.error(
        "PhoneChangeVerificationPopup: Phone change verification failed:",
        error,
      );

      if (error.response && error.response.status === 400) {
        const errorData = error.response.data;
        if (typeof errorData === "string") {
          setErrorMessage(errorData);
        } else if (errorData.message) {
          setErrorMessage(errorData.message);
        } else if (errorData.suggestion) {
          setErrorMessage(`${errorData.message} ${errorData.suggestion}`);
        } else {
          setErrorMessage(t("usermgmt.phone.invalidCode"));
        }
      } else if (error.response && error.response.status === 422) {
        setErrorMessage(t("usermgmt.phone.noPendingChangeShort"));
      } else {
        setErrorMessage(t("usermgmt.phone.verifyFailed"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    // Check if we have pending verification status and if phone change is actually pending
    if (
      pendingVerificationStatus &&
      !pendingVerificationStatus.hasPendingPhoneChange
    ) {
      setErrorMessage(t("usermgmt.phone.noPendingChange"));
      return;
    }

    setIsResending(true);
    setErrorMessage("");

    try {
      console.log(
        "PhoneChangeVerificationPopup: Resending phone change verification code",
      );

      // Ensure we have the pending phone number
      const pendingPhone = pendingVerificationStatus?.pendingPhone;
      if (!pendingPhone) {
        setErrorMessage(t("usermgmt.phone.noPendingPhone"));
        return;
      }

      const response =
        await PendingVerificationAPI.resendPhoneChangeCode(pendingPhone);
      console.log(
        "PhoneChangeVerificationPopup: Code resent successfully:",
        response.data || response,
      );

      toast(t("usermgmt.phone.codeSent"), { duration: 4000, icon: "ℹ️" });

      // Refresh the pending status
      const statusResponse =
        await PendingVerificationAPI.getPendingVerificationStatus();
      const refreshedData = statusResponse.data || statusResponse;
      setPendingVerificationStatus(refreshedData);
    } catch (error) {
      console.error(
        "PhoneChangeVerificationPopup: Failed to resend verification code:",
        error,
      );

      if (error.response && error.response.status === 400) {
        const errorData = error.response.data;
        if (errorData.message) {
          setErrorMessage(errorData.message);
        } else {
          setErrorMessage(t("usermgmt.phone.noPendingChangeShort"));
        }
      } else if (error.response && error.response.status === 422) {
        setErrorMessage(t("usermgmt.phone.noPendingChangeShort"));
      } else if (error.response && error.response.status === 503) {
        setErrorMessage(t("usermgmt.phone.sendCodeFailed"));
      } else {
        setErrorMessage(t("usermgmt.phone.resendFailed"));
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleCancelPhoneChange = async () => {
    try {
      console.log("PhoneChangeVerificationPopup: Cancelling phone change");
      const response = await PendingVerificationAPI.cancelPhoneChange();
      console.log(
        "PhoneChangeVerificationPopup: Phone change cancelled:",
        response.data || response,
      );

      toast(t("usermgmt.phone.cancelSuccess"), {
        duration: 3000,
        icon: "ℹ️",
      });
      onVerificationComplete(); // Refresh the parent component
      onClose();
    } catch (error) {
      console.error(
        "PhoneChangeVerificationPopup: Failed to cancel phone change:",
        error,
      );
      setErrorMessage(t("usermgmt.phone.cancelFailed"));
    }
  };

  const handleBackdropClick = (e) => {
    // Allow closing by clicking outside
    if (e.target.classList.contains("modal")) {
      onClose();
    }
  };

  return (
    <div className="phone-verification-modal" onClick={handleBackdropClick}>
      <div className="phone-verification-content">
        <div className="modal-header">
          <h2>{t("usermgmt.phone.modalTitle")}</h2>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="verification-popup-content">
          <p>
            <strong>
              {t("usermgmt.phone.hello").replace(
                "{firstName}",
                user?.firstName || t("usermgmt.phone.userFallback"),
              )}
            </strong>
          </p>
          <p>{t("usermgmt.phone.instructions")}</p>

          {pendingVerificationStatus && (
            <p>
              <strong>
                {pendingVerificationStatus.pendingPhone ||
                  t("usermgmt.phone.yourNewPhone")}
              </strong>
            </p>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="verificationCode">
                {t("usermgmt.phone.codeLabel")}
              </label>
              <input
                type="text"
                id="verificationCode"
                name="verificationCode"
                placeholder={t("usermgmt.phone.codePlaceholder")}
                value={verificationCode}
                onChange={handleChange}
                maxLength="6"
                pattern="[0-9]{6}"
                disabled={isLoading}
              />
            </div>

            {errorMessage && <p className="error-message">{errorMessage}</p>}

            <button type="submit" className="login-button" disabled={isLoading}>
              {isLoading
                ? t("usermgmt.phone.verifying")
                : t("usermgmt.phone.verifyBtn")}
            </button>
          </form>

          <div className="verification-actions">
            <button
              type="button"
              className="link-button"
              onClick={handleResendCode}
              disabled={isResending}
            >
              {isResending
                ? t("usermgmt.phone.sending")
                : t("usermgmt.phone.resendCode")}
            </button>

            <button
              type="button"
              className="link-button secondary"
              onClick={handleCancelPhoneChange}
            >
              {t("usermgmt.phone.cancelBtn")}
            </button>
          </div>

          <div className="verification-note">
            <p>
              <em>{t("usermgmt.phone.smsNote")}</em>
            </p>
            {pendingVerificationStatus && (
              <p>
                <em>
                  {t("usermgmt.phone.attempts")
                    .replace(
                      "{n}",
                      pendingVerificationStatus.phoneAttempts || 0,
                    )
                    .replace(
                      "{max}",
                      pendingVerificationStatus.maxAttempts || 5,
                    )}
                </em>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PhoneChangeVerificationPopup;
